<script setup>
import { reactive, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import FormField from 'dashboard/components/bookings/fields/FormField.vue';
import BookingTypeFields from 'dashboard/components/bookings/BookingTypeFields.vue';
import {
  BOOKING_TYPES,
  DEFAULT_BOOKING_TYPE,
  STATUS_OPTIONS,
} from 'dashboard/components/bookings/bookingTypes';
import {
  deriveTitle,
  deriveDates,
  deriveDurations,
} from 'dashboard/components/bookings/deriveBooking';
import {
  emptyDetails,
  missingPaths,
} from 'dashboard/components/bookings/bookingValidation';

const props = defineProps({
  contactId: { type: [String, Number], default: null },
  show: { type: Boolean, default: false },
  booking: { type: Object, default: null },
});

const emit = defineEmits(['update:show', 'close', 'saved']);

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS';

const initialState = () => ({
  title: '',
  bookingType: DEFAULT_BOOKING_TYPE,
  referenceNumber: '',
  status: STATUS_OPTIONS[0].value,
  startDate: '',
  endDate: '',
  totalAmount: '',
  currency: 'AED',
  details: emptyDetails(DEFAULT_BOOKING_TYPE),
});

const state = reactive(initialState());
const uploadedFiles = ref([]);
const isUploading = ref(false);
const isSubmitting = ref(false);
// Header fields the agent edited by hand; derivation leaves those alone.
const touched = ref(new Set());
// Bumped on every hydrate so the field set remounts with the freshly seeded
// details even when the same booking is reopened.
const formNonce = ref(0);

const isEdit = computed(() => Boolean(props.booking));

// The `*Fields.vue` components seed local state at setup, so the field set must
// remount whenever the edited booking or its type changes.
const fieldsKey = computed(
  () => `${props.booking?.id ?? 'new'}-${state.bookingType}-${formNonce.value}`
);

const typeOptions = BOOKING_TYPES.map(type => ({
  value: type.value,
  label: t(type.labelKey),
}));

const statusOptions = STATUS_OPTIONS.map(option => ({
  value: option.value,
  label: t(option.labelKey),
}));

const detailsComplete = value =>
  missingPaths(state.bookingType, value).length === 0;

const rules = computed(() => ({
  title: { required },
  details: { detailsComplete },
}));

const v$ = useVuelidate(rules, state);

const detailErrors = computed(() => {
  if (!v$.value.details.$error) return {};
  return Object.fromEntries(
    missingPaths(state.bookingType, state.details).map(path => [
      path,
      t(`${KEY}.FORM.REQUIRED`),
    ])
  );
});

const titleError = computed(() =>
  v$.value.title.$error ? t(`${KEY}.FORM.REQUIRED`) : ''
);

// Detached copy: the details object is edited in place by the form, the booking
// prop must not be.
const cloneDetails = details =>
  details ? JSON.parse(JSON.stringify(details)) : {};

const hydrate = () => {
  Object.assign(state, initialState());
  uploadedFiles.value = [];
  touched.value = new Set();
  formNonce.value += 1;
  if (!props.booking) return;

  const booking = props.booking;
  state.title = booking.title || '';
  state.bookingType = booking.booking_type || DEFAULT_BOOKING_TYPE;
  state.referenceNumber = booking.reference_number || '';
  state.status = booking.status || STATUS_OPTIONS[0].value;
  state.startDate = booking.start_date || '';
  state.endDate = booking.end_date || '';
  state.totalAmount = booking.total_amount || '';
  state.currency = booking.currency || 'AED';
  state.details = {
    ...emptyDetails(state.bookingType),
    ...cloneDetails(booking.details),
  };

  // A saved value that no longer matches its derivation was customised by hand.
  const derivedDates = deriveDates(state.bookingType, state.details);
  if (state.title !== deriveTitle(state.bookingType, state.details)) {
    touched.value.add('title');
  }
  if (state.startDate !== derivedDates.startDate) {
    touched.value.add('startDate');
  }
  if (state.endDate !== derivedDates.endDate) {
    touched.value.add('endDate');
  }
};

watch(
  () => [props.show, props.booking?.id],
  ([visible]) => {
    if (!visible) return;
    hydrate();
    v$.value.$reset();
  }
);

const onTypeChange = type => {
  state.bookingType = type;
  state.details = emptyDetails(type);
  // Anything derived for the old type is cleared here so the new type's empty
  // details never show stale values. A hand-edit belonged to the old type too,
  // so the whole set is released back to derivation.
  if (!touched.value.has('title')) state.title = '';
  if (!touched.value.has('startDate')) state.startDate = '';
  if (!touched.value.has('endDate')) state.endDate = '';
  touched.value = new Set();
};

const setTitle = title => {
  state.title = title;
  touched.value.add('title');
};

const setStartDate = date => {
  state.startDate = date;
  touched.value.add('startDate');
};

const setEndDate = date => {
  state.endDate = date;
  touched.value.add('endDate');
};

watch(
  () => [state.bookingType, state.details],
  () => {
    const title = deriveTitle(state.bookingType, state.details);
    const dates = deriveDates(state.bookingType, state.details);
    // Assigned unconditionally: clearing a source (a return leg, a drop-off
    // location) has to clear the derived header field too, or the stale value
    // would be saved.
    if (!touched.value.has('title')) state.title = title;
    if (!touched.value.has('startDate')) state.startDate = dates.startDate;
    if (!touched.value.has('endDate')) state.endDate = dates.endDate;
  },
  { deep: true }
);

const onClose = () => {
  emit('update:show', false);
  emit('close');
};

const onFileSelect = async event => {
  const files = Array.from(event.target.files);
  if (!files.length) return;

  isUploading.value = true;
  try {
    const uploads = files.map(file =>
      uploadFile(file, accountId.value).then(result => ({
        name: file.name,
        blobId: result.blobId,
      }))
    );
    const results = await Promise.all(uploads);
    uploadedFiles.value.push(...results);
  } finally {
    isUploading.value = false;
  }
};

const removeFile = index => {
  uploadedFiles.value.splice(index, 1);
};

const onSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;
  try {
    const bookingData = {
      title: state.title,
      booking_type: state.bookingType,
      reference_number: state.referenceNumber,
      status: state.status,
      start_date: state.startDate || null,
      end_date: state.endDate || null,
      total_amount: state.totalAmount || null,
      currency: state.currency,
      details: deriveDurations(state.bookingType, state.details),
      file_blob_ids: uploadedFiles.value.map(file => file.blobId),
    };

    const saved = isEdit.value
      ? await store.dispatch('contactBookings/update', {
          contactId: props.contactId,
          bookingId: props.booking.id,
          bookingData,
        })
      : await store.dispatch('contactBookings/create', {
          contactId: props.contactId,
          bookingData,
        });
    emit('saved', saved);
    onClose();
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <woot-modal
    :show="show"
    :on-close="onClose"
    :close-on-backdrop-click="false"
    class="!items-start [&>div]:!top-12 [&>div]:sticky"
  >
    <div class="flex w-full flex-col gap-4 px-6 py-6">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ isEdit ? t(`${KEY}.EDIT_BOOKING`) : t(`${KEY}.ADD_BOOKING`) }}
      </h3>

      <div class="grid grid-cols-2 gap-3">
        <FormField :label="t(`${KEY}.BOOKING_TYPE`)">
          <Select
            :model-value="state.bookingType"
            :options="typeOptions"
            class="!w-full"
            @update:model-value="onTypeChange"
          />
        </FormField>

        <FormField :label="t(`${KEY}.STATUS`)">
          <Select
            v-model="state.status"
            :options="statusOptions"
            class="!w-full"
          />
        </FormField>
      </div>

      <BookingTypeFields
        :key="fieldsKey"
        v-model="state.details"
        :booking-type="state.bookingType"
        :errors="detailErrors"
      />

      <div class="grid grid-cols-2 gap-3 border-t border-n-weak pt-4">
        <Input
          class="col-span-2"
          size="sm"
          :label="t(`${KEY}.TITLE`)"
          :placeholder="t(`${KEY}.PLACEHOLDER_TITLE`)"
          :model-value="state.title"
          :message="titleError"
          :message-type="titleError ? 'error' : 'info'"
          @update:model-value="setTitle"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.REFERENCE_NUMBER`)"
          :placeholder="t(`${KEY}.PLACEHOLDER_REF`)"
          :model-value="state.referenceNumber"
          @update:model-value="state.referenceNumber = $event"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.CURRENCY`)"
          :model-value="state.currency"
          @update:model-value="state.currency = $event"
        />
        <Input
          size="sm"
          type="date"
          :label="t(`${KEY}.START_DATE`)"
          :model-value="state.startDate"
          @update:model-value="setStartDate"
        />
        <Input
          size="sm"
          type="date"
          :label="t(`${KEY}.END_DATE`)"
          :model-value="state.endDate"
          @update:model-value="setEndDate"
        />
        <Input
          class="col-span-2"
          size="sm"
          type="number"
          step="0.01"
          :label="t(`${KEY}.TOTAL_AMOUNT`)"
          :placeholder="t(`${KEY}.PLACEHOLDER_AMOUNT`)"
          :model-value="state.totalAmount"
          @update:model-value="state.totalAmount = $event"
        />

        <!-- File Upload -->
        <div class="col-span-2">
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t(`${KEY}.FILES`) }}
          </label>
          <input
            type="file"
            multiple
            class="w-full text-sm text-n-slate-11 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-n-slate-12 hover:file:bg-n-alpha-3"
            @change="onFileSelect"
          />
          <div v-if="isUploading" class="mt-1 text-xs text-n-slate-11">
            {{ t(`${KEY}.UPLOADING`) }}
          </div>
          <div v-if="uploadedFiles.length" class="mt-2 flex flex-wrap gap-2">
            <span
              v-for="(file, index) in uploadedFiles"
              :key="index"
              class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
            >
              <span class="i-lucide-paperclip size-3" />
              {{ file.name }}
              <button
                class="ml-1 text-n-slate-11 hover:text-n-ruby-11"
                @click="removeFile(index)"
              >
                <span class="i-lucide-x size-3" />
              </button>
            </span>
          </div>
        </div>
      </div>

      <p v-if="v$.$error" class="text-xs text-n-ruby-9">
        {{ t(`${KEY}.FORM.INCOMPLETE`) }}
      </p>

      <div class="flex items-center justify-end gap-3 pt-2">
        <NextButton ghost :label="t(`${KEY}.CANCEL`)" @click="onClose" />
        <NextButton
          solid
          blue
          :label="isEdit ? t(`${KEY}.UPDATE`) : t(`${KEY}.SAVE`)"
          :is-loading="isSubmitting"
          :disabled="isSubmitting || isUploading"
          @click="onSubmit"
        />
      </div>
    </div>
  </woot-modal>
</template>
